import RequestHeadersBase from '~/common/constants/RequestHeadersBase';
import type { ConfirmPassword } from '~/domains/auth/interfaces/confirmPassword.interface';
import type { ForgotPassword } from '~/domains/auth/interfaces/forgotPassword.interface';
import type { EO_BRANDS, PLATFORMS } from '~/domains/auth/consts';

import { identityInstance } from './identity-instance';

const forgotPassword = async (input: ForgotPassword) =>
  identityInstance.post('/universal-auth/forgot-password', input);

const confirmPassword = async (input: ConfirmPassword) =>
  identityInstance.post('/universal-auth/confirm-password', input);

interface LogInInput {
  email: string;
  password: string;
  token?: string;
  destination?: keyof typeof EO_BRANDS;
  platform?: PLATFORMS;
}

interface AuthResponseBase {
  id: string;
  method: string;
  target: string;
}

interface AuthSuccessResponse extends AuthResponseBase {
  status: 'SUCCESS';
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  singleUseToken?: {
    token: string;
    expiresAt: Date;
  };
}

interface AuthMfaRequiredResponse extends AuthResponseBase {
  status: 'MULTI_FACTOR_REQUIRED';
  verificationToken: string;
  canResendIn?: number;
}

type AuthResponse = AuthSuccessResponse | AuthMfaRequiredResponse;

const logIn = async (input: LogInInput): Promise<AuthResponse> => {
  const response = await identityInstance.post('/universal-auth/login', input);

  return response.data;
};

interface SignUpInput {
  name?: string;
  email: string;
  password: string;
  referralCode?: string;
  referral?: string;
  token?: string;
  firstName?: string;
  lastName?: string;
  destination?: keyof typeof EO_BRANDS;
  platform?: PLATFORMS;
}

const signUp = async (input: SignUpInput): Promise<AuthResponse> => {
  const response = await identityInstance.post('/universal-auth/register', input);

  return response.data;
};

interface OtpVerifyInput {
  otp: string;
  verificationToken: string;
}

const verifyOtp = async (input: OtpVerifyInput): Promise<AuthSuccessResponse> => {
  const response = await identityInstance.post('/universal-auth/otp', input);

  return response.data;
};

interface OtpResendInput {
  verificationToken: string;
}

interface OtpResendResponse {
  canResendIn?: number;
}

const resendOtp = async (input: OtpResendInput): Promise<OtpResendResponse> => {
  const response = await identityInstance.post('/universal-auth/otp/resend', input);

  return response.data;
};

export const exchangeSingleUseTokenForAccessToken = async (singleUseToken: string) => {
  const response = await identityInstance.post(`/universal-auth/single-use-tokens`, undefined, {
    headers: {
      ...RequestHeadersBase,
      Authorization: `Bearer ${singleUseToken}`,
      'x-api-key': process.env.SINGLE_USE_AUTH_API_KEY,
    },
  });

  return response.data;
};

export const getReferralCode = async (referralCode: string) => {
  const response = await identityInstance.get(`/referral-codes/${referralCode}`);

  return response.data;
};

export {
  confirmPassword,
  forgotPassword,
  logIn,
  resendOtp,
  signUp,
  verifyOtp,
};

export type { AuthMfaRequiredResponse, AuthResponse, AuthSuccessResponse, LogInInput };
